import { Controller } from "@hotwired/stimulus"
import {
  enableOfflineStorage,
  getOfflineSyncStatus,
  isStandalonePwa,
  OFFLINE_SYNC_EVENT
} from "../pwa/snapshot"

// 事件一覧と設定画面で共有する、端末内の同期状態表示。状態確認は通信しない。
export default class extends Controller {
  static targets = ["button", "status", "checkedAt"]
  static values = { messages: Object, locale: String }

  connect() {
    this.standalone = isStandalonePwa()
    this.element.hidden = !this.standalone
    this.element.style.display = this.standalone ? "" : "none"
    if (!this.standalone) return
    this.connected = true
    this.version = 0
    this.refresh = this.refresh.bind(this)
    window.addEventListener(OFFLINE_SYNC_EVENT, this.refresh)
    window.addEventListener("online", this.refresh)
    window.addEventListener("offline", this.refresh)
    this.refresh()
  }

  disconnect() {
    this.connected = false
    window.removeEventListener(OFFLINE_SYNC_EVENT, this.refresh)
    window.removeEventListener("online", this.refresh)
    window.removeEventListener("offline", this.refresh)
    window.clearTimeout(this.retryTimer)
  }

  async refresh() {
    const version = ++this.version
    try {
      const state = await getOfflineSyncStatus()
      if (!this.connected || version !== this.version) return
      this.render(state)
    } catch (_error) {
      if (!this.connected || version !== this.version) return
      this.buttonTarget.disabled = true
      this.statusTarget.textContent = this.message("failed")
    }
  }

  render(state) {
    window.clearTimeout(this.retryTimer)
    const busy = this.updating || state.syncing
    const waiting = state.retryAfter > Date.now()
    this.blocked = busy || waiting || !navigator.onLine
    this.buttonTarget.disabled = this.blocked
    this.buttonTarget.textContent = this.message(state.identity.enabled ? "update" : "enable")
    this.element.setAttribute("aria-busy", busy ? "true" : "false")
    const snapshot = state.snapshot
    this.checkedAtTarget.textContent = snapshot?.syncedAt
      ? this.message("checked_at", { time: this.stamp(snapshot.syncedAt) }) : ""

    let message
    if (!navigator.onLine) {
      message = this.message("offline")
    } else if (busy) {
      message = state.progress ? this.message("progress", state.progress) : this.message("checking")
    } else if (waiting) {
      message = this.message("waiting", { time: this.stamp(state.retryAfter) })
    } else if (snapshot && Date.parse(snapshot.expiresAt) > Date.now()) {
      message = snapshot.storedCount < snapshot.urlCount
        ? this.message("partial", { stored: snapshot.storedCount, total: snapshot.urlCount })
        : this.message("ready")
    } else {
      message = this.message("empty")
    }
    this.statusTarget.textContent = message
    // 期限後はボタンだけ戻す。タイマーから同期を開始しない。
    if (waiting && !busy) {
      this.retryTimer = window.setTimeout(this.refresh, Math.min(state.retryAfter - Date.now() + 1, 2_147_483_647))
    }
  }

  async update(event) {
    event.preventDefault()
    if (!this.standalone || this.updating || this.blocked || !navigator.onLine) return
    this.updating = true
    this.buttonTarget.disabled = true
    this.statusTarget.textContent = this.message("checking")
    let failed = false
    try {
      await enableOfflineStorage()
    } catch (_error) {
      failed = true
    } finally {
      this.updating = false
      await this.refresh()
      if (failed && this.connected) this.statusTarget.textContent = this.message("failed")
    }
  }

  message(key, values = {}) {
    return (this.messagesValue[key] || "").replace(/%\{(\w+)\}/g, (_, name) => String(values[name] ?? ""))
  }

  stamp(value) {
    return new Date(value).toLocaleString(this.localeValue || "ja", {
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit"
    })
  }
}
